import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import Box from '@mui/material/Box/Box';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import TwField, { TwInputTypes } from '../../components/TwFields/TwFields';
import TwDialog from '../../components/TwDialog/TwDialog';
import useGooglePlacesAutocomplete from '../../hooks/useGooglePlacesAutocomplete';
import AuthService from '../../providers/Auth/Auth.service';
import { AccountData } from '../../providers/Settings/Account.model';
import SettingsService from '../../providers/Settings/Settings.service';
import CreateLocation from '../Locations/CreateLocation/CreateLocation';
import { UserResponse } from '../../providers/User/User.model';
import LoadsService from '../../providers/Loads/Loads.service';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import './EditCompany.scss';
import { LoadLocation } from '../../redux/api/load/load.model';
import { CreateLocationData } from '../../redux/api/location/location.model';
import { logRocketTrack } from '../../utils/logRocketUtils';
import { AmplitudeEvent, logAmplitudeEvent } from '../../utils/amplitudeUtils';

export interface EditCompanyProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (userInfo: UserResponse) => void;
  onNewLocation?: (location: LoadLocation) => void;
}

const EditCompany: React.FC<EditCompanyProps> = ({
  open,
  onClose,
  onSuccess,
  onNewLocation
}): JSX.Element => {
  const { init: initAutocomplete } = useGooglePlacesAutocomplete();
  const { addNotification } = useNotifications();

  const autocompleteInputRef = useRef<HTMLInputElement | undefined>();
  const isAutocompleteInUseRef = useRef<boolean>(false);
  const [googleAutocompleteText, setGoogleAutocompleteText] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [companyEmail, setCompanyEmail] = useState<string>('');
  const [companyPhone, setCompanyPhone] = useState<string>('');

  const [userData, setUserData] = useState<UserResponse | undefined>(undefined);

  // Manually added location
  const [companyLocationOpen, setCompanyLocationOpen] = useState(false);
  const [companyCreatedLocation, setCompanyCreatedLocation] = useState<LoadLocation | undefined>(
    undefined
  );

  // Google place data
  const [placeId, setPlaceId] = useState<string>('');
  const [addressLine, setAddressLine] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [zip, setZip] = useState<string>('');
  const [placeName, setPlaceName] = useState<string>('');

  /** * Gets account data */
  const getAccountInfo = useCallback(async () => {
    try {
      const userData = await AuthService.getCurrentUser();
      setUserData(userData);
    } catch (error) {
      logRocketTrack('Invoice', {
        action: 'Edit bill from info',
        severity: 'high',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Error: could not get information, please try again',
        variant: 'error'
      });
    }
  }, []);

  const onGooglePlaceChanged = (place?: google.maps.places.PlaceResult): void => {
    if (!place || !place.address_components) return;
    isAutocompleteInUseRef.current = true;

    logAmplitudeEvent(AmplitudeEvent.GOOGLE_PLACES_REQUEST);
    let address = '';
    let city = '';
    let state = '';
    let zip = '';

    for (const component of place.address_components) {
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number':
          address = `${component.long_name} ${address}`;
          break;
        case 'route':
          address += component.short_name;
          break;
        case 'postal_code':
          zip = `${component.long_name}${zip}`;
          break;
        case 'locality':
          city = component.long_name;
          break;
        case 'administrative_area_level_1':
          state = component.short_name;
          break;
      }
    }

    setGoogleAutocompleteText(place.formatted_address || '');

    setCity(city);
    setZip(zip);
    setState(state);
    setAddressLine(place.formatted_address || '');
    setPlaceName(place.name || `${place.formatted_address || address}, ${city}, ${state}`);
    setPlaceId(place.place_id || '');

    isAutocompleteInUseRef.current = false;
  };

  /** Submits the new information for the user company */
  const handleSubmit = (): void => {
    if (!companyName) {
      logRocketTrack('Invoice', {
        action: 'Edit bill from info',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Company name is missing',
        variant: 'error',
        timeToShow: 5000
      });
      return;
    }

    if (!companyCreatedLocation?.id && !addressLine && !userData?.tenant?.location) {
      logRocketTrack('Create invoice', {
        action: 'Edit company address',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      addNotification({
        message: 'Please enter an address for your company',
        variant: 'error',
        timeToShow: 5000
      });
      return;
    }

    if (
      addressLine &&
      googleAutocompleteText &&
      !companyCreatedLocation &&
      !userData?.tenant?.location?.full_address.includes(addressLine)
    ) {
      const data: CreateLocationData = {
        name: placeName,
        address_1: addressLine,
        state: state
      };

      city ? (data.city = city) : null;
      zip ? (data.zip = zip) : null;

      if (placeId !== '') {
        data.google_place_id = placeId;
      }

      LoadsService.createLocation(data)
        .then((newLocation) => {
          const params: AccountData = {
            company_name: companyName,
            location_id: newLocation.id,
            accounting_email: companyEmail
          };

          companyPhone ? (params.phone = companyPhone) : null;

          SettingsService.updateAccount(params)
            .then((userInfo) => {
              onSuccess(userInfo);
              handleClose();
            })
            .catch((error) => alert(error.message));
        })
        .catch((error) => alert(error.message));
    } else {
      const params: AccountData = {
        company_name: companyName,
        accounting_email: companyEmail,
        phone: companyPhone || null
      };

      companyCreatedLocation?.id ? (params.location_id = companyCreatedLocation?.id) : null;

      SettingsService.updateAccount(params)
        .then((userInfo) => {
          onSuccess(userInfo);
          handleClose();
        })
        .catch((error) => alert(error.message));
    }
  };

  const handleClose = (): void => {
    setGoogleAutocompleteText('');
    setCompanyCreatedLocation(undefined);
    setUserData(undefined);
    setAddressLine('');
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    (async (): Promise<void> => {
      await getAccountInfo();
    })();
  }, [open]);

  useEffect(() => {
    if (!userData) return;

    userData?.tenant?.location &&
      setGoogleAutocompleteText(userData?.tenant?.location.address_1 || '');
    setCompanyName(userData.company_name);
    setCompanyEmail(userData.tenantAccountingSettings?.accounting_email || '');
    setCompanyPhone(userData.phone?.toString() || '');
  }, [userData]);

  useEffect(() => {
    if (!open) return;

    const autocompleteTimeout = setTimeout(() => {
      // Init Google autocomplete
      initAutocomplete({
        inputRef: autocompleteInputRef,
        onPlaceChanged: onGooglePlaceChanged
      });

      autocompleteInputRef.current?.focus();
    }, 100);

    return (): void => {
      clearTimeout(autocompleteTimeout);
    };
  }, [open]);

  useEffect(() => {
    companyCreatedLocation && onNewLocation && onNewLocation(companyCreatedLocation);
  }, [companyCreatedLocation]);

  return (
    <>
      <TwDialog
        open={open}
        onClickClose={handleClose}
        size="sm"
        title="Edit your company info"
        actions={[
          <TwButton
            key="1"
            fullwidth={false}
            type={TwButtonType.secondary}
            label="Cancel"
            action={handleClose}
          />,
          <TwButton
            key="2"
            fullwidth={false}
            type={TwButtonType.primary}
            label="Save"
            action={handleSubmit}
          />
        ]}
      >
        <div className="edit-company__wrapper tid-edit-company__wrapper">
          <div className="edit-company__form-section">
            <div className="edit-company__inputs-container">
              <div className="tid-edit-company__company-name">
                <TwField
                  id="companyName"
                  name="companyName"
                  value={companyName}
                  label="Company name"
                  inputType={TwInputTypes.text}
                  disabled={false}
                  onInputChange={(e): void => {
                    setCompanyName(e.target.value);
                  }}
                  error={false}
                />
              </div>

              <span className="edit-company__field-name">Company address</span>
              {companyCreatedLocation && companyCreatedLocation.address_1 && (
                <span className="edit-company__new-location-address">
                  {companyCreatedLocation.address_1 + ' ' + companyCreatedLocation.address_2}
                </span>
              )}

              <div className="tid-edit-company__company-address">
                {!companyCreatedLocation && (
                  <TwField
                    inputRef={autocompleteInputRef}
                    id="edit-company-address-autocomplete"
                    name="address-autocomplete"
                    placeholder="Search for address..."
                    value={googleAutocompleteText}
                    onInputChange={(e): void => setGoogleAutocompleteText(e.target.value)}
                    autoComplete="off"
                    startAdorment={
                      <InputAdornment position="start">
                        <SearchIcon height={14} />
                      </InputAdornment>
                    }
                  />
                )}

                <Box className="edit-company__add-location">
                  Can{"'"}t find a location?{' '}
                  <Box
                    component="a"
                    onClick={(): void => {
                      setCompanyLocationOpen(true);
                    }}
                  >
                    Add it manually
                  </Box>
                </Box>
              </div>

              <div className="edit-company__two-column">
                <div
                  className="tid-edit-company__company-email
                  edit-company__two-column-item"
                >
                  <TwField
                    id="companyEmail"
                    name="companyEmail"
                    value={companyEmail}
                    label="Accounting Email"
                    inputType={TwInputTypes.text}
                    onInputChange={(e): void => {
                      setCompanyEmail(e.target.value);
                    }}
                    error={false}
                  />
                </div>

                <div
                  className="tid-edit-company__company-phone 
                  edit-company__two-column-item"
                >
                  <TwField
                    id="companyPhone"
                    name="companyPhone"
                    value={companyPhone}
                    label="Phone number (optional)"
                    inputType={TwInputTypes.number}
                    disabled={false}
                    onInputChange={(e): void => {
                      setCompanyPhone(e.target.value);
                    }}
                    error={false}
                  />
                </div>
              </div>
            </div>
            <CreateLocation
              open={companyLocationOpen}
              onSuccess={(location): void => {
                setCompanyCreatedLocation(location);
              }}
              onClose={(): void => setCompanyLocationOpen(false)}
              extraInfo={true}
            />
          </div>
        </div>
      </TwDialog>
    </>
  );
};

export default EditCompany;
